/** Entitlement and redemption operations (spec §11). */

import { z } from "zod";
import { publicOp, readOp, writeOp, zEpochMs, zId, zMoney, zNonNegMoney, zRecipient } from "./common.js";

const zEntitlementState = z.enum(["ISSUED", "ACTIVE", "EXHAUSTED", "EXPIRED", "VOIDED"]);
/**
 * B406 — the summary grows DISPLAY facts in the B145/B155 pattern, resolved
 * read-time and honestly absent when a store cannot name them: variantName
 * (via the order line that issued the entitlement — the line index is in the
 * id the confirmation minted) and admittedAt (the §8.2 standing scan's time,
 * so a reversed scan un-says it). Receipt facts for the ticket's own
 * audience; the undo HANDLE stays redemption.activity's (B200).
 */
const zEntitlementSummary = z
  .object({
    entitlementId: zId,
    state: zEntitlementState,
    variantName: z.string().min(1).optional(),
    admittedAt: zEpochMs.optional(),
    /** B633 — the NAME of the device that made that standing scan (B627),
     * STAFF readers only; absent for anyone else and for an unnamed device. */
    admittedBy: z.string().min(1).optional(),
  })
  .strict();

export const entitlementList = readOp({
  name: "entitlement.list",
  owner: "customer",
  permission: "entitlement:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:VALIDATOR", "DEVICE:KIOSK", "DEVICE:PRINTER"],
  fields: { orderId: zId.optional(), customerId: zId.optional(), slotId: zId.optional() },
  response: z.object({ entitlements: z.array(zEntitlementSummary) }).strict(),
  exampleRequest: { orderId: "o-1" },
  exampleResponse: { entitlements: [{ entitlementId: "e-1", state: "ISSUED" }] },
});

export const entitlementGet = readOp({
  name: "entitlement.get",
  owner: "customer",
  permission: "entitlement:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:VALIDATOR", "DEVICE:KIOSK", "DEVICE:PRINTER"],
  fields: { entitlementId: zId },
  response: z.object({ entitlement: zEntitlementSummary.optional() }).strict(),
  exampleRequest: { entitlementId: "e-1" },
  exampleResponse: { entitlement: { entitlementId: "e-1", state: "ISSUED" } },
});

/**
 * B20b(e) ruling 2026-07-14: the confirmation page renders its own QR (§8.1)
 * — a CUSTOMER-audience read for the signed ticket token. Minted through
 * ticketPayloadFor, the ONE mint path (B28): this read, the delivery email,
 * and the manifest row are byte-equal strings. VOIDED refuses; absent and
 * foreign are one uniform 404.
 */
export const entitlementToken = readOp({
  name: "entitlement.token",
  owner: "customer",
  permission: "entitlement:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:PRINTER"],
  fields: { entitlementId: zId },
  response: z.object({ entitlementId: zId, token: z.string().min(1) }).strict(),
  exampleRequest: { entitlementId: "e-1" },
  exampleResponse: { entitlementId: "e-1", token: "ttk1.eyJleGFtcGxlIjp0cnVlfQ.key-1.c2ln" },
});

/** Re-delivery only — no state change (§11). */
export const entitlementResend = writeOp({
  name: "entitlement.resend",
  permission: "entitlement:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  /* B608 — the destination is an email or the door refuses it: before, a
     `min(1)` bound let `maya@` ride to the wire and the mail layer inherit
     a non-address. zRecipient is the one home the admin field judge reads. */
  fields: { entitlementId: zId, destination: zRecipient },
  response: z.object({ entitlementId: zId, queued: z.literal(true) }).strict(),
  exampleRequest: { entitlementId: "e-1", destination: "guest@example.com" },
  exampleResponse: { entitlementId: "e-1", queued: true },
});

const zVerdict = z.enum(["ADMIT", "DENY", "WARN"]);
const zVerdictReason = z.enum([
  "ok",
  "expired",
  "not_yet_active",
  "already_used",
  "voided",
  "wrong_slot",
  "wrong_leg",
  "offline_balance_unverified",
  // B704 (2) — member cards: a tier blackout day, and guests past the member's daily allowance.
  "blackout",
  "guest_limit",
]);

/**
 * B704 (2) — the member gate's two new refusals carry the sentence the face
 * prints as written ("Not valid today — Zoo Membership excludes Halloween
 * Spooktacular"; "Only 1 guest left today — Duo brings 2 a day"), and an
 * admitted member card under the guestsPerDay switch answers `guestsLeft`.
 * Present ONLY on member-card answers — the handler holds that (walled in its
 * tests), not a refinement (the B479 stance).
 */
const zRefusal = z.string().min(1).optional();
const zGuestsLeft = z.number().int().nonnegative().optional();
/** B704 (3) — compare the photo ID before admitting (the tenant's photoId switch, this card never checked; 586:154's CHECK ID). */
const zCheckId = z.literal(true).optional();
/** Accompanying guests walking in with a member card (B704 (2)); read only for member cards. */
const zGuests = z.number().int().min(0).max(10).optional();

/**
 * B479 — an `already_used` verdict names WHEN the use was spent (`redeemedAt`, the
 * standing REDEEM record's instant — a reversed spend does not count). The gate's
 * face reads "Already used · Redeemed 12:51", not a bare refusal (Richard, 2026-09-17).
 * Present ONLY with `already_used`: the HANDLER holds that rule (walled in its tests),
 * not a refinement — refinements are ZodEffects the B22 membrane cannot translate.
 */
const zAlreadyUsedAt = zEpochMs.nonnegative().optional();

/**
 * B549 (8) — a GROUP PASS's headcount (448:6): the seats the pass carries,
 * how many are already on board, what is left; plus the party's name and the
 * departure where the groups desk is wired. Present ONLY on a group pass —
 * every other ticket keeps the bare verdict shape.
 */
const zGroupHeadcount = z
  .object({
    seats: z.number().int().positive(),
    onBoard: z.number().int().nonnegative(),
    remaining: z.number().int().nonnegative(),
    organisation: z.string().min(1).optional(),
    visitAt: zEpochMs.optional(),
  })
  .strict();

/**
 * B628 (a) — the ticket beside the verdict, the ticket page's own words: the
 * show, the showtime in the tenant's clock, the label ("General Admission ·
 * 1 of 2"), and the buyer's name for STAFF principals only. Absent where the
 * stage does not wire the facts — the verdict never waits on them.
 */
const zDoorTicket = z
  .object({ show: z.string().optional(), when: z.string().optional(), label: z.string(), holder: z.string().optional() })
  .strict();

/**
 * B704 (6a) — the member card's facts beside the verdict (586:154's card):
 * number and since ("Mar 2024"), the tier's entry, the named adults (their
 * `names` for STAFF principals only — B628), the household children the tier
 * covers, today's guests against the allowance (only under the guestsPerDay
 * switch), guest passes left, and the household's last visit (a day word on
 * the venue's clock). Present ONLY on a member card's answer — refusals too,
 * so the NOT TODAY face can name whose card.
 */
const zCount = z.number().int().nonnegative();
const zDoorMember = z
  .object({
    number: z.string().min(1),
    since: z.string().min(1),
    entry: z.enum(["ALL_DAYS", "LIMITED"]).optional(),
    adults: z.number().int().positive(),
    names: z.array(z.string().min(1)).optional(),
    children: z.object({ max: zCount, ages: z.object({ min: zCount, max: zCount }).strict().optional() }).strict().optional(),
    guests: z.object({ used: zCount, perDay: zCount }).strict().optional(),
    guestPasses: zCount,
    lastVisit: z.string().min(1).optional(),
  })
  .strict();

/** Read-only verdict — never mutates (§8.2), which is why it is a read. */
export const redemptionValidate = readOp({
  name: "redemption.validate",
  permission: "redeem:execute",
  principals: ["STAFF:VALIDATOR", "STAFF:MANAGER", "STAFF:ADMIN", "DEVICE:VALIDATOR"],
  fields: { token: z.string().min(1), scanSlotId: zId.optional(), at: zEpochMs.optional(), guests: zGuests },
  response: z
    .object({
      verdict: zVerdict,
      reason: zVerdictReason,
      redeemedAt: zAlreadyUsedAt,
      group: zGroupHeadcount.optional(),
      ticket: zDoorTicket.optional(),
      member: zDoorMember.optional(),
      refusal: zRefusal,
      guestsLeft: zGuestsLeft,
      checkId: zCheckId,
    })
    .strict(),
  exampleRequest: { token: "tok-1" },
  exampleResponse: { verdict: "ADMIT", reason: "ok" },
});

/**
 * B628 (c), the 2026-10-06 recorded default — a typed CONFIRMATION CODE at
 * the gate: the order's tickets on the gate's showtime (an un-slotted pass
 * is valid at any gate and rides too), label + status + the facts the ADMIT
 * face already draws — never the buyer's email. Gate principals only,
 * rate-limited per principal in the handler; misses answer one uniform
 * empty list (a short code must not become an oracle — the B115 stance).
 * B670's office door (LET SOMEONE IN by code) rides the same answer.
 */
export const redemptionLookup = readOp({
  name: "redemption.lookup",
  permission: "redeem:read",
  principals: ["STAFF:VALIDATOR", "STAFF:MANAGER", "STAFF:ADMIN", "DEVICE:VALIDATOR"],
  fields: { code: z.string().min(1).max(40), scanSlotId: zId.optional() },
  response: z
    .object({
      tickets: z.array(
        z
          .object({
            entitlementId: zId,
            label: z.string(),
            /** The entitlement machine's own state word (ISSUED, ACTIVE, REDEEMED, VOID…). */
            status: z.string(),
            show: z.string().optional(),
            when: z.string().optional(),
            /** The standing admit's instant, where one exists. */
            redeemedAt: zEpochMs.optional(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: { code: "MZKW-7BPF", scanSlotId: "s-1" },
  exampleResponse: {
    tickets: [{ entitlementId: "o-1 t1", label: "General Admission · 1 of 2", status: "ISSUED", show: "Cedric The Entertainer", when: "Fri, Oct 9 · 7:00 PM" }],
  },
});

export const redemptionRedeem = writeOp({
  name: "redemption.redeem",
  permission: "redeem:execute",
  principals: ["STAFF:VALIDATOR", "STAFF:MANAGER", "STAFF:ADMIN", "DEVICE:VALIDATOR"],
  fields: {
    entitlementId: zId,
    redemptionId: zId,
    deviceId: zId,
    staffId: zId,
    at: zEpochMs,
    scanSlotId: zId.optional(),
    serviceDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    guests: zGuests,
  },
  response: z
    .object({
      verdict: zVerdict,
      reason: zVerdictReason,
      remaining: z.number().int().nonnegative().optional(),
      redeemedAt: zAlreadyUsedAt,
      ticket: zDoorTicket.optional(),
      member: zDoorMember.optional(),
      refusal: zRefusal,
      guestsLeft: zGuestsLeft,
      checkId: zCheckId,
    })
    .strict(),
  exampleRequest: { entitlementId: "e-1", redemptionId: "r-1", deviceId: "gate-1", staffId: "staff-1", at: 1_780_000_000_000 },
  exampleResponse: { verdict: "ADMIT", reason: "ok", remaining: 0 },
});

/**
 * B549 (8) — the headcount face's FINISH · ADMIT k: k exactly-once §8.2
 * spends of ONE group pass in one press (each head its own derived
 * redemption id, so a retried press admits nobody twice). A count above the
 * pass's balance admits nobody (409); late-comers are a second press.
 */
export const redemptionGroupAdmit = writeOp({
  name: "redemption.group.admit",
  permission: "redeem:execute",
  principals: ["STAFF:VALIDATOR", "STAFF:MANAGER", "STAFF:ADMIN", "DEVICE:VALIDATOR"],
  fields: {
    entitlementId: zId,
    redemptionId: zId,
    count: z.number().int().min(1).max(999),
    deviceId: zId,
    staffId: zId,
    at: zEpochMs,
    scanSlotId: zId.optional(),
  },
  response: z
    .object({
      verdict: zVerdict,
      reason: zVerdictReason,
      admitted: z.number().int().nonnegative(),
      seats: z.number().int().positive(),
      onBoard: z.number().int().nonnegative(),
      remaining: z.number().int().nonnegative(),
    })
    .strict(),
  exampleRequest: { entitlementId: "o-grp-G-1 pass", redemptionId: "r-1", count: 12, deviceId: "gate-1", staffId: "staff-1", at: 1_780_000_000_000 },
  exampleResponse: { verdict: "ADMIT", reason: "ok", admitted: 12, seats: 40, onBoard: 12, remaining: 28 },
});

export const redemptionReverse = writeOp({
  name: "redemption.reverse",
  permission: "redeem:execute",
  principals: ["STAFF:VALIDATOR", "STAFF:MANAGER", "STAFF:ADMIN", "DEVICE:VALIDATOR"],
  fields: { redemptionId: zId, reverseRedemptionId: zId, deviceId: zId, staffId: zId, at: zEpochMs },
  response: z.object({ redemptionId: zId, reversed: z.literal(true) }).strict(),
  exampleRequest: { redemptionId: "r-1", reverseRedemptionId: "r-2", deviceId: "gate-1", staffId: "staff-1", at: 1_780_000_060_000 },
  exampleResponse: { redemptionId: "r-1", reversed: true },
});

export const manifestDownload = readOp({
  name: "manifest.download",
  permission: "redeem:read",
  principals: ["STAFF", "DEVICE:VALIDATOR"],
  fields: { deviceId: zId, slotId: zId.optional(), serviceDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional() },
  response: z
    .object({
      manifestUrl: z.string().url(),
      expiresAt: zEpochMs,
      revokedEntitlementIds: z.array(zId),
      /** B615 leg 3c — the slot's policy facts in the door's voice (the door list's own derivation), plus the
       * age alone for the admit face. ADDITIVE and UNSIGNED: words for the host, never an admission rule —
       * the signed document decides who enters. Absent when nothing is set. */
      doorFacts: z
        .object({ minAge: z.number().int().min(1).max(99).optional(), lines: z.array(z.string().min(1).max(200)).max(20) })
        .strict()
        .optional(),
    })
    .strict(),
  exampleRequest: { deviceId: "gate-1", slotId: "s-1" },
  exampleResponse: { manifestUrl: "https://example.com/m/s-1", expiresAt: 1_780_003_600_000, revokedEntitlementIds: [] },
});

const zOfflineScan = z
  .object({
    sequence: z.number().int().nonnegative(),
    entitlementId: zId,
    direction: z.enum(["REDEEM", "REVERSE"]),
    at: zEpochMs,
    scanSlotId: zId.optional(),
    serviceDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  })
  .strict();

/** Idempotent on (device id, sequence) — a retried sync never double-redeems (§8.3). */
export const redemptionSync = writeOp({
  name: "redemption.sync",
  permission: "redeem:execute",
  principals: ["STAFF:VALIDATOR", "STAFF:MANAGER", "STAFF:ADMIN", "DEVICE:VALIDATOR"],
  fields: { deviceId: zId, scans: z.array(zOfflineScan).min(1) },
  response: z
    .object({
      accepted: z.number().int().nonnegative(),
      duplicates: z.number().int().nonnegative(),
      flagged: z.number().int().nonnegative(),
    })
    .strict(),
  exampleRequest: { deviceId: "gate-1", scans: [{ sequence: 0, entitlementId: "e-1", direction: "REDEEM", at: 1_780_000_000_000 }] },
  exampleResponse: { accepted: 1, duplicates: 0, flagged: 0 },
});

/**
 * B74 R4 (docs/OPERATIONS.md §7 R4; §8, §4.6) — scan activity for the ops
 * drawer and the no-show/scan-rate metrics. Scope is EXACTLY ONE of
 * { slotId } (the departure) or { day } (a §4.6 service day); both or
 * neither is refused by name. MANAGER/ADMIN only: scan state is
 * back-office truth (the B20b stance — gate crew redeems, it does not
 * read the office's books). Slot-basis summary: sold counts the slot's
 * non-VOIDED entitlements (a revocation is not a sold seat), scanned
 * counts effective scans (§8.2: a REDEEM uncompensated by its REVERSE —
 * the booking.list semantics exactly), reversed counts REVERSE records,
 * lastScanAt is device activity (max REDEEM.at, reversed or not). Day
 * scope adds untimed serviceDay activity SEPARATELY (`untimed`, entry
 * basis SERVICE_DAY, summarized from that day's records only) so
 * sold ≥ scanned stays coherent — sold-per-day for a pass is a sales
 * question (R3), not a no-show question. flagged surfaces §8.3 offline
 * admissions still standing. Entries walk the entitlementId order behind
 * a versioned cursor; unknown ≡ foreign ≡ empty (Inv 10).
 *
 * B200 (answer-shape amendment, B20a-style) — each entry additionally
 * names its STANDING scans (§8.2 uncompensated REDEEMs) by the scan's own
 * id + at, oldest first, absent when nothing stands: exactly the handle
 * redemption.reverse requires, so an undo can be OFFERED from recent
 * activity instead of asking an operator to copy a reference off a
 * screen. Audience deliberately unchanged (MANAGER/ADMIN — the B92
 * stance): the picker degrades by role at the client, never by widening
 * a read.
 */
const zScanServiceDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const zScanCount = z.number().int().nonnegative();
/**
 * B222 — the untimed (SERVICE_DAY) half of a day's scan activity.
 *
 * `sold` was missing for as long as this block existed: the handler
 * computed `untimedAgg.sold` and dropped it at the response literal, so
 * a consumer folding the day together had untimed SCANS and no untimed
 * denominator to put them over. The dashboard folded them over the
 * SLOT-only sold anyway and its Scanning tile could report over 100% —
 * telling an operator they scanned more tickets than they sold on any
 * day with day-pass activity. Optional because a bundle built before
 * this amendment must keep parsing (B168's additive-evolution stance);
 * the handler always answers it now.
 */
const zUntimedSummary = z
  .object({ sold: zScanCount.optional(), scanned: zScanCount, reversed: zScanCount, lastScanAt: zEpochMs.optional() })
  .strict();
export const redemptionActivity = readOp({
  name: "redemption.activity",
  permission: "redeem:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    slotId: zId.optional(),
    day: zScanServiceDay.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(500).optional(),
  },
  response: z
    .object({
      sold: zScanCount,
      scanned: zScanCount,
      reversed: zScanCount,
      lastScanAt: zEpochMs.optional(),
      untimed: zUntimedSummary.optional(),
      entries: z.array(
        z
          .object({
            entitlementId: zId,
            orderId: zId,
            productId: zId,
            state: zEntitlementState,
            basis: z.enum(["SLOT", "SERVICE_DAY"]),
            scanned: z.boolean(),
            redeems: zScanCount,
            reversed: zScanCount,
            lastScanAt: zEpochMs.optional(),
            flagged: z.boolean().optional(),
            standingScans: z
              .array(z.object({ redemptionId: zId, at: zEpochMs }).strict())
              .min(1)
              .optional(),
            /**
             * B717 — the ticket in words, on entries with a standing scan only:
             * the office's UNDO A SCAN picker names the scan by its ticket
             * (label · show · showtime · booking code), never by this entry's
             * entitlementId. Never a holder. Absent when the read failed.
             */
            ticket: z
              .object({ label: z.string(), show: z.string().optional(), when: z.string().optional(), code: z.string().optional() })
              .strict()
              .optional(),
          })
          .strict(),
      ),
      cursor: z.string().min(1).optional(),
    })
    .strict(),
  exampleRequest: { slotId: "s-1" },
  exampleResponse: {
    sold: 3,
    scanned: 1,
    reversed: 0,
    lastScanAt: 1_780_000_000_000,
    entries: [
      {
        entitlementId: "e-1",
        orderId: "o-1",
        productId: "sail",
        state: "EXHAUSTED",
        basis: "SLOT",
        scanned: true,
        redeems: 1,
        reversed: 0,
        lastScanAt: 1_780_000_000_000,
        standingScans: [{ redemptionId: "r-1", at: 1_780_000_000_000 }],
      },
    ],
  },
});

/**
 * B74 R8 (docs/OPERATIONS.md §7 R8; §8.2, §8.3) — the OFFICE's printable
 * manifest. Staff-auth, NO device binding — the R8 point: the device door
 * stays manifest.download's. Scope is EXACTLY ONE of { slotId } (that
 * departure's sheet) or { serviceDay } (one document, a section per §4.6
 * slot — the R4 fan-out); both or neither refused by name. Content is the
 * device manifest's §8.2 selection exactly (one partition function feeds
 * both doors): admittable ISSUED/ACTIVE rows + the VOIDED revocation
 * list, EXHAUSTED/EXPIRED in neither. Day passes bind to no slot and are
 * deliberately NOT listed — the day document says so honestly. Rendered
 * HTML lands in the manifest object store behind a presigned URL; no
 * token material is printed (paper carries ids, gates carry tokens).
 * MANAGER/ADMIN only: the passenger list is back-office truth (B20b).
 */
export const manifestPrint = readOp({
  name: "manifest.print",
  permission: "redeem:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    slotId: zId.optional(),
    serviceDay: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  },
  response: z
    .object({
      manifestUrl: z.string().url(),
      expiresAt: zEpochMs,
      generatedAt: zEpochMs,
      slots: z.array(
        z
          .object({
            slotId: zId,
            entries: z.number().int().nonnegative(),
            revoked: z.number().int().nonnegative(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: { slotId: "s-1" },
  exampleResponse: {
    manifestUrl: "https://example.com/m/print-s-1.html",
    expiresAt: 1_780_003_600_000,
    generatedAt: 1_780_000_000_000,
    slots: [{ slotId: "s-1", entries: 42, revoked: 1 }],
  },
});

/* ------------------------------------------- B606 (d) the ticket page */

const zTicketPage = z
  .object({
    /** The seller's trading name, as the email signs. */
    seller: z.string().min(1).optional(),
    status: zEntitlementState,
    /** B617 leg 2 — `bill`: who is on ("Cedric The Entertainer · hosted by Ali Siddiq"), when the show has a lineup. */
    ticket: z.object({ label: z.string().min(1), bill: z.string().min(1).optional(), when: z.string().min(1).optional() }).strict(),
    order: z
      .object({
        lines: z.array(z.object({ label: z.string().min(1), quantity: z.number().int().positive(), unitPrice: zMoney, net: zMoney }).strict()),
        fees: z.array(z.object({ label: z.string().min(1), amount: zMoney }).strict()),
        tax: zMoney.optional(),
        total: zMoney.optional(),
        confirmationCode: z.string().min(1).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

/**
 * B606 (d) — RULED 2026-10-01 (Pathik: "storefront link makes sense"; the
 * design seat ruled it as recommended): the storefront ticket page's one
 * read. PUBLIC — the ttk1 ticket token in the page's fragment is the
 * credential. Answers ONE ticket's status, label and departure plus its
 * order's summary; never the buyer, never a token; the uniform `{}` for
 * anything unverifiable (docs/ABUSE-CONTROLS.md § ticket.open).
 */
export const ticketOpen = publicOp({
  name: "ticket.open",
  permission: "public",
  fields: { token: z.string().min(8).max(4096) },
  response: z.object({ page: zTicketPage.optional() }).strict(),
  exampleRequest: { token: "ttk1.eyJleGFtcGxlIjp0cnVlfQ.key-1.c2ln" },
  exampleResponse: {
    page: {
      seller: "Harbor Ferry",
      status: "ISSUED",
      ticket: { label: "Adult · 1 of 2", when: "Sun 4 Oct · 10:30" },
      order: {
        lines: [{ label: "Harbor Ferry crossing — Adult", quantity: 2, unitPrice: { amount: 2500, currency: "USD" }, net: { amount: 5000, currency: "USD" } }],
        fees: [{ label: "Booking fee", amount: { amount: 300, currency: "USD" } }],
        total: { amount: 5300, currency: "USD" },
        confirmationCode: "HF7K-2Q9M",
      },
    },
  },
});


/**
 * B633a (RULED by the design seat 2026-10-06, adopting the 2026-10-04 00:57Z
 * recommendation; Pathik may overrule) — VOID A TICKET from the manage-booking
 * page: a ONE-ticket refund through today's refund path, naming which ticket,
 * with $0 allowed. With `amount` > 0 the door stages an ordinary refund
 * request (one line, this ticket named) that the Refunds queue executes —
 * money only ever moves through request → execute, and the void happens at
 * execute. With `amount` absent or zero the ticket voids NOW with no journal
 * (a void moves no money, so it adds no new ledger meaning); the timeline
 * records `ticket_voided` with the reason. A redeemed or already-void ticket
 * refuses; an absent or foreign one is the uniform 404 (Inv 10).
 */
export const entitlementVoid = writeOp({
  name: "entitlement.void",
  permission: "refund:write",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    entitlementId: zId,
    reason: z.string().min(1).max(500),
    /** Money to return alongside the void; absent or zero = void only. */
    amount: zNonNegMoney.optional(),
  },
  response: z
    .object({
      entitlementId: zId,
      /** True when the ticket voided NOW ($0 path); false when the void waits on the staged refund's execute. */
      voided: z.boolean(),
      refund: z.object({ refundId: zId, status: z.literal("REQUESTED") }).strict().optional(),
    })
    .strict(),
  exampleRequest: { entitlementId: "o-1 0 1", reason: "no-show", amount: { amount: 2835, currency: "USD" } },
  exampleResponse: { entitlementId: "o-1 0 1", voided: false, refund: { refundId: "ref-1", status: "REQUESTED" } },
});
